import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Auth } from "aws-amplify";
import { Formik, Form, Field } from "formik";
import {
  FormControl,
  FormGroup,
  Button,
  Spinner,
  Toast,
} from "react-bootstrap";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("*Invalid email").required("*Email Required"),
  code: Yup.string()
    .min(6, "*Code must have at least 6 characters")
    .required("*Code is required"),
});

const validAuthStates = ["accountConfirm"];

const CustomConfirmAccount = ({ authState, onStateChange, account }) => {
  const [showToast, setShowToast] = useState(false);
  const [emailAccount, setEmailAccount] = useState(account.email);
  const [resendCodeStatus, setResendCodeStatus] = useState({
    isProcessing: false,
    message: "",
    isError: false,
  });
  const [confirmSignupStatus, setConfirmSignupStatus] = useState({
    isProcessing: false,
    message: "",
  });

  const toggleToast = () => {
    setShowToast(!showToast);
  };

  useEffect(() => {
    // Set the account's email
    setEmailAccount(account.email);
  }, [account]);

  useEffect(() => {
    // Reset status of confirm-account form
    setResendCodeStatus({ isProcessing: false, message: "", isError: false });

    setConfirmSignupStatus({
      isProcessing: false,
      message: "",
    });
  }, [authState]);

  const handleResendCode = async (e) => {
    e.preventDefault();

    if (!emailAccount) {
      setResendCodeStatus({
        isProcessing: false,
        message: "Email is required",
        isError: true,
      });
      return;
    }

    // Standalone checking email using Yup-helpers
    if (!Yup.string().email().isValidSync(emailAccount)) {
      setResendCodeStatus({
        isProcessing: false,
        message: "Invalid email!",
        isError: true,
      });
      return;
    }

    try {
      setResendCodeStatus({ isProcessing: true, message: "" });

      await Auth.resendSignUp(emailAccount);

      setResendCodeStatus({
        isProcessing: false,
        message: "We've just sent code to your email.",
        isError: false,
      });
    } catch (e) {
      setResendCodeStatus({
        isProcessing: false,
        message: e.message || "Fail to resend code. Please try again!",
        isError: true,
      });
    }
  };

  const confirmAccountSubmit = async (values) => {
    const { email, code } = values;
    try {
      setConfirmSignupStatus({ isProcessing: true, message: "" });

      await Auth.confirmSignUp(email, code);

      setConfirmSignupStatus({
        isProcessing: false,
        message: "",
      });

      // Go to sign-in form after verify success
      onStateChange("signIn");
      toggleToast();
    } catch (e) {
      setConfirmSignupStatus({
        isProcessing: false,
        message: e.message || "Fail to confirm account. Please try again!",
      });
    }
  };

  return (
    <>
      <Toast show={showToast} onClose={toggleToast} autohide>
        <Toast.Body>Your account was confirmed successfully!</Toast.Body>
      </Toast>
      {validAuthStates.includes(authState) ? (
        <Formik
          initialValues={{
            email: account.email,
            code: "",
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={confirmAccountSubmit}
        >
          {({ errors, touched }) => (
            <Form className="auth-form">
              <Field name="email">
                {({ field }) => (
                  <FormGroup controlId="email">
                    <FormControl
                      type="email"
                      placeholder="Email"
                      className="auth-input"
                      value={field.value}
                      onChange={(e) => {
                        setEmailAccount(e.target.value);
                        field.onChange(e);
                      }}
                      isValid={touched.email && !errors.email}
                      isInvalid={touched.email && errors.email}
                      data-testid="email-confirm-account"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.email}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>
              <Field name="code">
                {({ field }) => (
                  <FormGroup controlId="code">
                    <FormControl
                      type="text"
                      placeholder="Verification Code"
                      className="auth-input"
                      value={field.value}
                      onChange={field.onChange}
                      isValid={touched.code && !errors.code}
                      isInvalid={touched.code && errors.code}
                      data-testid="code-confirm-account"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.code}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>

              <div>
                <Button
                  type="submit"
                  disabled={confirmSignupStatus.isProcessing}
                >
                  Submit
                  {confirmSignupStatus.isProcessing && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="isProcessing"
                      className="ml-1"
                      aria-hidden="true"
                    />
                  )}
                </Button>
                {confirmSignupStatus.message && (
                  <p className="error-message mt-1 mb-0">
                    {confirmSignupStatus.message}
                  </p>
                )}
                <Button
                  variant="link"
                  className="auth-navigate"
                  onClick={handleResendCode}
                  disabled={resendCodeStatus.isProcessing}
                  data-testid="resend-code"
                >
                  <em>Resend code</em>
                  {resendCodeStatus.isProcessing && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="isProcessing"
                      className="ml-1"
                      aria-hidden="true"
                    />
                  )}
                </Button>
                {resendCodeStatus.message && (
                  <p
                    className={resendCodeStatus.isError ? "error-message" : ""}
                  >
                    {resendCodeStatus.message}
                  </p>
                )}
                {/*
                <p>
                  {`Don't have an account?`}{" "}
                  <Button
                    variant="link"
                    onClick={() => onStateChange("signUp")}
                  >
                    <strong>Sign up</strong>
                  </Button>
                </p>
                */}
              </div>
            </Form>
          )}
        </Formik>
      ) : null}
    </>
  );
};

CustomConfirmAccount.propTypes = {
  authState: PropTypes.string,
  onStateChange: PropTypes.func,
  account: PropTypes.object,
};

export default CustomConfirmAccount;
